import { Dispatch, SetStateAction, useMemo } from "react";
import Form from "react-bootstrap/esm/Form";
// components
import RichTextEditor from "components/rich-text-editor/RichTextEditor";
import DiagnosisCode from "../diagnosis-code";
import SimpleTextFormGroup from "../simple-text-form-group";
import PlanFieldLabel from "../plan-field-label";
import ReminderFields from "../reminder-fields/ReminderFields";
// constants
import { RICH_TEXT_FIELDS, SIMPLE_TEXT_FIELDS } from "../constants";
import messages from "messages";
// utils
import getClasses from "utils/getClasses";
// styles
import styles from "./styles.module.scss";

type Props = {
  readOnly?: boolean;
  code: ICDCode & { id: string; [key: string]: any };
  value: TreatmentPlan;
  setValue?: Dispatch<SetStateAction<TreatmentPlan>>;
};

const BasicPlanFields = ({
  code,
  value,
  setValue,
  readOnly = false,
}: Props) => {
  const blockClass = useMemo(
    () =>
      getClasses(
        styles["basic-plan-fields__block"],
        readOnly && styles["basic-plan-fields__block--readOnly"],
      ),
    [readOnly],
  );
  return (
    <>
      {/* icd code  */}
      {!readOnly && <DiagnosisCode className={blockClass} code={code} />}

      {/* simple text field editor */}
      {Object.entries(SIMPLE_TEXT_FIELDS).map(([key, label], i) => (
        <SimpleTextFormGroup
          readOnly={readOnly}
          controlId={key}
          key={key}
          className={blockClass}
          label={label}
          value={value[key]}
          onChange={(e) =>
            setValue &&
            setValue((s) => ({
              ...s,
              [key]: e.target.value,
            }))
          }
          isTextArea={true}
          placeholder={messages.enterYourDescription}
        />
      ))}

      {/* rich text field editor */}
      {Object.entries(RICH_TEXT_FIELDS).map(([key, label], i) => (
        <Form.Group key={key} className={blockClass}>
          <PlanFieldLabel label={label} readOnly={readOnly} />
          <RichTextEditor
            className={
              !readOnly ? "" : styles["basic-plan-fields__rich-text--readOnly"]
            }
            readOnly={readOnly}
            id={key + i}
            key={key}
            value={value[key]}
            onChange={(v) =>
              setValue &&
              setValue((s) => ({
                ...s,
                [key]: v,
              }))
            }
            bounds={`.${styles["basic-plan-fields__block"]}`}
            placeholder={messages.enterYourDescription}
          />
        </Form.Group>
      ))}
      {/* reminder section */}
      <ReminderFields
        className={blockClass}
        readOnly={readOnly}
        value={value}
        setValue={setValue}
      />
    </>
  );
};

export default BasicPlanFields;
